import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import StatCounter from "@/components/StatCounter";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import {
  ArrowUpRight,
  ArrowDown,
  MapPin,
  Building2,
  ShieldCheck,
  Briefcase,
  Mail,
  LineChart,
  GitMerge,
  Heart,
  Lock,
  Sprout,
  Compass,
} from "lucide-react";
import { insights } from "@/data/insights";

const explore = [
  { to: "/ueber-uns", label: "Über uns", text: "Profil, Mission und Dachstruktur der Beteiligungsgesellschaft.", icon: Building2, testId: "explore-about" },
  { to: "/investmentfokus", label: "Investmentfokus", text: "Sechs Felder unternehmerischer Beteiligungen aus München.", icon: Briefcase, testId: "explore-fields" },
  { to: "/ablauf", label: "Ablauf", text: "Vom Erstgespräch bis zur langfristigen Partnerschaft – in sechs Schritten.", icon: GitMerge, testId: "explore-process" },
  { to: "/kontakt", label: "Kontakt", text: "Vertrauliche Gespräche für Partner und Projektentwickler.", icon: Mail, testId: "explore-contact" },
];

const reasons = [
  { icon: LineChart, title: "Langfristiges Denken", text: "Wir investieren in Jahrzehnten, nicht in Quartalen. Substanz schlägt Geschwindigkeit." },
  { icon: ShieldCheck, title: "Unternehmerische Verantwortung", text: "Wir verstehen uns als aktiver Eigentümer mit Verantwortung gegenüber Mitarbeitenden, Partnern und Region." },
  { icon: Sprout, title: "Nachhaltiges Wachstum", text: "Wachstum entsteht aus Struktur, Disziplin und realer Marktnachfrage – nicht aus kurzfristiger Inszenierung." },
  { icon: Heart, title: "Verlässliche Partnerschaften", text: "Wir bauen Beziehungen, die belastbar sind – mit Inhabern, Vermietern, Banken, Beratern und Mitstreitern." },
  { icon: Lock, title: "Diskretion", text: "Alle Anfragen und Beteiligungen werden strikt vertraulich behandelt. Standard, kein Bonus." },
  { icon: Compass, title: "Strategische Entwicklung", text: "Wir bringen Strategie, Struktur und unternehmerische Energie ein – pragmatisch und entschlossen." },
];

const stats = [
  { value: 6, label: "Investmentfelder" },
  { value: 100, suffix: " %", label: "Eigenkapitalbasis" },
  { value: 10, suffix: "+", label: "Jahre Anlagehorizont" },
];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  } catch (e) {
    return iso;
  }
}

export default function Home() {
  useRevealOnScroll();
  const teaser = insights.slice(0, 3);

  return (
    <div className="bg-black text-white min-h-screen">
      <SEO
        title="Wagenhofer Capital | Leo Wagenhofer | Beteiligungen & Unternehmensentwicklung"
        description="Wagenhofer Capital ist die Beteiligungs- und Unternehmensstruktur von Leo Wagenhofer mit Fokus auf strategische Beteiligungen, operative Entwicklung und langfristigen Vermögensaufbau."
        path="/"
      />
      <Navigation />

      {/* HERO */}
      <section
        id="home"
        data-testid="section-hero"
        className="relative min-h-screen flex items-center overflow-hidden pt-28 grain"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1595867818082-083862f3d630?auto=format&fit=crop&w=2400&q=80')",
            filter: "grayscale(45%)",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.82) 60%, #000 100%)",
          }}
          aria-hidden
        />
        <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/25 to-transparent" />

        <div className="relative container-x w-full">
          <div className="max-w-4xl">
            <div className="reveal flex items-center gap-4 mb-8">
              <span className="block w-8 h-px bg-[#D4AF37]" />
              <span className="overline text-zinc-400 flex items-center gap-2">
                <MapPin className="w-3 h-3" strokeWidth={1.5} />
                München · Beteiligungs- und Verwaltungsgesellschaft
              </span>
            </div>
            <h1
              data-testid="hero-headline"
              className="reveal delay-1 font-display text-[3.2rem] leading-[1.02] sm:text-6xl lg:text-[5.5rem] tracking-tighter font-medium text-white mb-8"
            >
              Wagenhofer
              <br />
              <span className="text-zinc-300">Capital</span>
            </h1>
            <p data-testid="hero-subheadline" className="reveal delay-2 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed font-light mb-6">
              Strategische Beteiligungen. Unternehmerische Entwicklung. Langfristiger Vermögensaufbau.
            </p>
            <p className="reveal delay-2 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed font-light mb-12">
              Wagenhofer Capital wurde von <span className="text-zinc-200">Leo Wagenhofer</span> gegründet und steht für unternehmerische Entwicklung, strategische Beteiligungen und langfristigen Vermögensaufbau. Beteiligungen, Immobilien, Gastronomie, Events und Dienstleistungen werden unter einer klaren Dachstruktur im Raum München gebündelt.
            </p>
            <div className="reveal delay-3 flex flex-col sm:flex-row gap-4">
              <Link to="/kontakt" data-testid="hero-cta-contact" className="group inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-zinc-200 transition-all duration-300 px-8 py-4 uppercase tracking-[0.28em] text-[11px] font-semibold">
                Kontakt aufnehmen
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              </Link>
              <Link to="/investmentfokus" data-testid="hero-cta-fields" className="group inline-flex items-center justify-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-8 py-4 uppercase tracking-[0.28em] text-[11px] font-semibold">
                Investmentfokus ansehen
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
          <div className="hidden md:flex absolute bottom-10 right-6 md:right-12 lg:right-20 items-center gap-3 text-zinc-500 text-xs tracking-[0.28em] uppercase">
            <ArrowDown className="w-4 h-4 animate-pulse" strokeWidth={1.5} />
            Weiter
          </div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="section-stats" className="border-t border-white/5 py-16 md:py-24 bg-[#070707]">
        <div className="container-x grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
          {stats.map((s, i) => (
            <div key={i} className="reveal" style={{ transitionDelay: `${i * 70}ms` }}>
              <div className="overline mb-3 text-zinc-500">0{i + 1}</div>
              <div className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white">
                <StatCounter value={s.value} suffix={s.suffix || ""} />
              </div>
              <div className="mt-3 text-sm text-zinc-400">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="section-why" className="section-y border-t border-white/5">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
            <div className="md:col-span-6 reveal">
              <div className="overline mb-6">Warum Wagenhofer Capital</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.08]">
                Grundwerte einer
                <br />
                <span className="text-zinc-400">unternehmerischen Holding.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 reveal delay-1 text-zinc-400 text-base md:text-lg leading-relaxed font-light">
              Substanz, Struktur, Verantwortung. Die Grundlagen, auf denen wir Beteiligungen, Immobilien und Projekte führen – jeden Tag.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/5">
            {reasons.map((r, idx) => {
              const Icon = r.icon;
              return (
                <article
                  key={r.title}
                  data-testid={`reason-${idx + 1}`}
                  className="group relative p-10 md:p-12 border-r border-b border-white/5 hover:bg-[#0a0a0a] transition-colors duration-500 reveal"
                  style={{ transitionDelay: `${idx * 60}ms` }}
                >
                  <span className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-8" strokeWidth={1.25} />
                  <h3 className="font-display text-xl tracking-tight font-medium mb-4">{r.title}</h3>
                  <p className="text-zinc-400 leading-relaxed font-light text-[15px]">{r.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXPLORE */}
      <section data-testid="section-explore" className="section-y border-t border-white/5 bg-[#070707]">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
            <div className="md:col-span-6 reveal">
              <div className="overline mb-6">Übersicht</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.08]">
                Erkunden Sie
                <br />
                <span className="text-zinc-400">Wagenhofer Capital.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 reveal delay-1 text-zinc-400 text-base md:text-lg leading-relaxed font-light">
              Vier zentrale Einstiegspunkte – wählen Sie den Bereich, der Ihrem Anliegen entspricht.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {explore.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className="group relative p-10 md:p-12 border-r border-b border-white/10 hover:bg-black transition-colors duration-500 reveal"
                  style={{ transitionDelay: `${idx * 70}ms` }}
                >
                  <span className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="flex items-center justify-between mb-8">
                    <Icon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.25} />
                    <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium mb-3">{item.label}</h3>
                  <p className="text-zinc-400 leading-relaxed font-light text-sm">{item.text}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* INSIGHTS TEASER */}
      <section data-testid="section-insights-teaser" className="section-y border-t border-white/5">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="reveal">
              <div className="overline mb-4">Insights</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.08]">
                Perspektiven<br/><span className="text-zinc-400">aus der Praxis.</span>
              </h2>
            </div>
            <Link to="/insights" className="reveal delay-1 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] hover:text-white transition-colors">
              Alle Beiträge
              <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
            {teaser.map((article, i) => (
              <Link
                key={article.slug}
                to={`/insights/${article.slug}`}
                className="group bg-black hover:bg-[#0a0a0a] transition-colors duration-500 p-8 reveal"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[16/10] overflow-hidden mb-6 border border-white/5">
                  <img src={article.image} alt={article.title} title={article.title} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                </div>
                <div className="flex items-center gap-3 mb-3 text-[11px] text-zinc-500 tracking-[0.2em] uppercase">
                  <span className="text-[#D4AF37]">{article.category}</span>
                  <span>·</span>
                  <span>{formatDate(article.date)}</span>
                </div>
                <h3 className="font-display text-lg md:text-xl tracking-tight font-medium leading-snug mb-2">{article.title}</h3>
                <p className="text-zinc-400 leading-relaxed font-light text-sm">{article.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
